import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';

import { notification } from 'antd';
import Axios from 'axios';

import { ListContext } from '@contexts/ListContext';

import { useFollowup, forceReloadFollowup } from '@hooks/API/Event/UseFollowup';
import useEventStyle from '@hooks/CachedLists/UseEventStyle';

import AddModelsToNonBookingEvent from '@components/EventDrawer/BookingEvent/AddModels/AddModelsToNonBookingEvent';
import EventDrawerDetailsContainer from '@components/EventDrawer/EventDrawerUi/EventDrawerDetailsContainer';
import { EventContainer } from '@components/EventDrawer/EventDrawerUi/EventUI';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ProjectEventsTimeline from '@components/Project/ProjectEventsTimeline/ProjectEventsTimeline';

import AddDatesToFollowUp from './AddDatesToFollowUp';
import FollowUpContent from './FollowUpContent';
import { FollowUpStateContext, FollowUpDispatchContext, followUpReducer, ACTIONS } from './FollowUpContext';
import FollowUpFormEdit from './FollowUpFormEdit';
import FollowUpFormEditGroupInstance from './FollowUpFormEditGroupInstance';
import FollowUpFormNew from './FollowUpFormNew';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FollowUpWrapper = ({
    afterRemove,
    afterUpdate,
    allDay,
    dateSelected,
    eventInfo,
    filterModel,
    ID,
    modelId,
    onClose,
    onSelectEvent,
    onTouchForm,
    plainMode,
    projectId,
    viewMode,
}) => {
    const [followUpID, setFollowUpID] = useState(ID);
    const { data: followUpData, isFetching, status } = useFollowup(followUpID);
    const [viewDetails, setViewDetails] = useState(false);
    const [touchForm, setTouchForm] = useState(false);

    const [reducerState, dispatch] = useReducer(followUpReducer, {
        isEdit: viewMode ? true : false,
        editGroupInstance: undefined,
        showAddModelDrawer: false,
        showAddDatesDrawer: false,
        addCommentForEventId: null,
    });
    const { isEdit, editGroupInstance, showAddModelDrawer, showAddDatesDrawer, addCommentForEventId } = reducerState;

    const groupType = useMemo(() => {
        if (followUpData?.PeriodGroupGuid) {
            return 'period';
        }
        if (followUpData?.GroupGuid) {
            return 'models';
        }
        return null;
    }, [followUpData]);

    const { periodFirstDate, periodLastDate } = useMemo(() => {
        if (!followUpData) {
            return { periodFirstDate: null, periodLastDate: null };
        }
        if (groupType === 'period' && followUpData.RelatedEvents?.length > 0) {
            const allStartDates = [followUpData.StartDate, ...followUpData.RelatedEvents.map((e) => e.StartDate)];
            const allEndDates = [followUpData.EndDate, ...followUpData.RelatedEvents.map((e) => e.EndDate)];
            return {
                periodFirstDate: allStartDates.reduce((min, date) => (date < min ? date : min)),
                periodLastDate: allEndDates.reduce((max, date) => (date > max ? date : max)),
            };
        }
        return {
            periodFirstDate: followUpData.StartDate,
            periodLastDate: followUpData.EndDate,
        };
    }, [followUpData, groupType]);

    // FollowUp style and labels
    const followUpStyle = useEventStyle({}, 'FollowUp');
    const { cachedList } = useContext(ListContext);
    const followUpLabels = cachedList.followUpLabels.items || [];
    const followUpSubType = useMemo(() => {
        if (!followUpData?.FollowUpLabelCode) return null;
        return followUpLabels.find((item) => item.Code === followUpData.FollowUpLabelCode);
    }, [followUpLabels, followUpData?.FollowUpLabelCode]);

    const handleClearEditEvent = useCallback(() => {
        if (editGroupInstance) {
            dispatch({ type: ACTIONS.CLEAR_EDIT_GROUP_INSTANCE });
        }
    }, [editGroupInstance, dispatch]);

    const handleClearTouchForm = useCallback(() => {
        if (touchForm) {
            setTouchForm(false);
        }
    }, [touchForm]);

    const handleCancel = useCallback(() => {
        handleClearEditEvent();
        handleClearTouchForm();
        if (followUpData) {
            dispatch({ type: ACTIONS.SET_IS_EDIT, payload: false });
        } else {
            onClose();
        }
    }, [handleClearEditEvent, handleClearTouchForm, followUpData, dispatch, onClose]);

    const followUpStateValue = useMemo(
        () => ({
            groupType,
            followUpData,
            isEdit,
            editGroupInstance,
            showAddModelDrawer,
            showAddDatesDrawer,
            addCommentForEventId,
            afterUpdate,
            afterRemove,
            followUpID,
            projectId,
            modelId,
            plainMode,
            onCancel: handleCancel,
            followUpStyle,
            followUpLabels,
            followUpSubType,
            periodFirstDate,
            periodLastDate,
        }),
        [
            groupType,
            followUpData,
            isEdit,
            editGroupInstance,
            showAddModelDrawer,
            showAddDatesDrawer,
            addCommentForEventId,
            afterUpdate,
            afterRemove,
            followUpID,
            projectId,
            modelId,
            plainMode,
            handleCancel,
            followUpStyle,
            followUpLabels,
            followUpSubType,
            periodFirstDate,
            periodLastDate,
        ]
    );

    useEffect(() => {
        setViewDetails(false);
    }, [filterModel]);

    useEffect(() => {
        if (ID) {
            setFollowUpID(ID);
        }
    }, [ID]);

    const handleSelectProject = async (project) => {
        try {
            //await onSetTravelProject({ travelId: travelsData.ID, projectId: project });
            const err = await Axios.put('/followups/SetProject', {
                ObjectID: followUpData.ID,
                ProjectID: project,
            });
            forceReloadFollowup(followUpData.ID);
            await afterUpdate();
        } catch (error) {
            if (error.errorCode && error.errorCode === 'GenericError') {
                notification.error({
                    duration: 0,
                    message: 'Error',
                    description: error.message,
                    icon: <NwIcon icon={byPrefixAndName.fass['exclamation-circle']} />,
                });
            }
        }
    };

    const handleTouchForm = (touched) => {
        if (followUpData) {
            setTouchForm(touched);
        } else {
            onTouchForm(touched);
        }
    };

    const handleUpdateFollowupData = (id) => {
        if (id) {
            setFollowUpID(id);
        } else {
            forceReloadFollowup(followUpID);
        }
        handleClearTouchForm();
        handleClearEditEvent();
    };

    if ((!followUpData && followUpID) || status === 'Loading') {
        return <Loading textBlack />;
    }

    // Create new followup
    if (!followUpData && isEdit) {
        return (
            <FollowUpDispatchContext.Provider value={dispatch}>
                <FollowUpStateContext.Provider value={followUpStateValue}>
                    <EventContainer>
                        <div className="ant-drawer-header ant-drawer-header-sticky ">
                            <div className="ant-drawer-title">Add new activity</div>
                        </div>
                        <FollowUpFormNew
                            allDay={allDay}
                            dateSelected={dateSelected}
                            eventInfo={eventInfo}
                            onTouchForm={handleTouchForm}
                            onUpdateFollowupData={handleUpdateFollowupData}
                        />
                    </EventContainer>
                </FollowUpStateContext.Provider>
            </FollowUpDispatchContext.Provider>
        );
    }

    return (
        <FollowUpDispatchContext.Provider value={dispatch}>
            <FollowUpStateContext.Provider value={followUpStateValue}>
                {plainMode ? ( //activity card in the project page
                    <EventContainer hasProject={followUpData && followUpData.Project}>
                        <div className="event-content-wrapper">
                            {!plainMode && followUpData.Project && (
                                <ProjectEventsTimeline
                                    event={{
                                        ...followUpData,
                                        TypeName: 'Travel',
                                    }}
                                    projectID={followUpData.Project.ID}
                                    onSelectEvent={onSelectEvent}
                                />
                            )}
                            <FollowUpContent
                                filterModel={filterModel}
                                handleSelectProject={handleSelectProject}
                                onTouchForm={onTouchForm}
                                setViewDetails={setViewDetails}
                                viewDetails={viewDetails}
                            />
                        </div>
                    </EventContainer>
                ) : (
                    <EventDrawerDetailsContainer
                        eventData={{ ...followUpData, elType: 'FollowUp', uniqID: `FollowUp-${followUpData.ID}` }}
                        filterModel={modelId}
                        isFetching={isFetching}
                        typeName="followup"
                        onSelectEvent={onSelectEvent}
                        onSelectProject={handleSelectProject}
                        firstTab={
                            <div className="event-content-wrapper">
                                {!plainMode && followUpData.Project && (
                                    <ProjectEventsTimeline
                                        event={{
                                            ...followUpData,
                                            TypeName: 'FollowUp',
                                        }}
                                        projectID={followUpData.Project.ID}
                                        onSelectEvent={onSelectEvent}
                                    />
                                )}
                                <FollowUpContent
                                    filterModel={filterModel}
                                    handleSelectProject={handleSelectProject}
                                    onTouchForm={onTouchForm}
                                    setViewDetails={setViewDetails}
                                    viewDetails={viewDetails}
                                />
                            </div>
                        }
                    />
                )}
                {isEdit && (
                    <NwDrawer
                        padding="0px"
                        width={740}
                        title="Edit Activity"
                        touchForm={touchForm}
                        onTouchForm={setTouchForm}
                        onClose={() => {
                            handleClearEditEvent();
                            dispatch({ type: ACTIONS.SET_IS_EDIT, payload: false });
                            handleClearTouchForm();
                        }}
                    >
                        <EventContainer>
                            {editGroupInstance ? (
                                <FollowUpFormEditGroupInstance
                                    onTouchForm={handleTouchForm}
                                    onUpdateFollowupData={handleUpdateFollowupData}
                                    viewMode={viewMode}
                                />
                            ) : (
                                <FollowUpFormEdit
                                    onTouchForm={handleTouchForm}
                                    onUpdateFollowupData={handleUpdateFollowupData}
                                    viewMode={viewMode}
                                />
                            )}
                        </EventContainer>
                    </NwDrawer>
                )}
                {showAddModelDrawer && (
                    <AddModelsToNonBookingEvent
                        event={followUpData}
                        projectId={projectId || (followUpData.Project && followUpData.Project.ID)}
                        onUpdateEvent={() => {
                            forceReloadFollowup(followUpData.ID);
                            afterUpdate();
                        }}
                        eventType="FollowUp"
                        onClose={() => dispatch({ type: ACTIONS.SET_ADD_MODEL_DRAWER, payload: false })}
                    />
                )}
                {showAddDatesDrawer && <AddDatesToFollowUp />}
            </FollowUpStateContext.Provider>
        </FollowUpDispatchContext.Provider>
    );
};

export default FollowUpWrapper;
